import type { ReactNode } from 'react';
import React from 'react';
import { createPortal } from 'react-dom';

import { sizing } from '../../../common/sizing';
import { useShellLayerScrollContext } from '../../../frame/Shell/utils';
import { styled } from '../../../stitches.config';
import { useFieldErrorMessage } from './useFieldErrorMessage';

const FieldErrorMessagesStuck = styled('div', {
  transition: 'opacity 200ms ease-in-out, transform 200ms ease-in-out',

  variants: {
    position: {
      top: {
        top: 0,
        transform: 'translateY(0)',
      },
      bottom: {
        bottom: 0,
        transform: 'translateY(0)',
      },
    },
    visible: {
      true: {
        opacity: 1,
        pointerEvents: 'auto',
      },
      false: {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translateY(100%)',
      },
    },
    hasScrollContainer: {
      true: {
        position: 'absolute',
        right: sizing.primary,
        left: sizing.primary,
      },
      false: {
        position: 'fixed',
        right: 0,
        left: 0,
      },
    },
  },

  compoundVariants: [
    {
      position: 'top',
      visible: false,
      css: {
        transform: 'translateY(-100%)',
      },
    },
  ],
});
FieldErrorMessagesStuck.displayName = 'FieldErrorMessagesStuck';

const FieldErrorMessagesOriginal = styled('div', {
  position: 'relative',

  variants: {
    isStuck: {
      top: {
        '& > *': {
          borderRadiusBottom: '$10',
        },
      },
      bottom: {
        '& > *': {
          borderRadiusTop: '$10',
        },
      },
      none: {},
    },
  },
});
FieldErrorMessagesOriginal.displayName = 'FieldErrorMessagesOriginal';

export function FieldErrorMessagesPortal({ children }: { children: React.ReactNode }) {
  // Find the drawer content container instead of the drawer portal
  const mount = document.querySelector('[data-drawer-content]');

  if (!mount) {
    return null;
  }

  return createPortal(children, mount);
}

interface FieldErrorMessagesProps {
  /**
   * The error messages to display.
   */
  children: ReactNode;
  /**
   * Whether to enable intelligent sticky behavior
   * @default true
   */
  enabled?: boolean;
  /**
   * Scroll container to observe (defaults to window)
   */
  scrollContainer?: HTMLElement | null;
}

function FieldErrorMessagesContent({
  children,
  enabled = true,
  scrollContainer = null,
}: FieldErrorMessagesProps) {
  const contextScrollContainer = useShellLayerScrollContext().scrollContainer;
  const finalScrollContainer = scrollContainer || contextScrollContainer;

  const { isStuck, stickyPosition, originalRef, stuckRef, zIndex } = useFieldErrorMessage({
    scrollContainer: finalScrollContainer,
  });

  if (!enabled) {
    return children;
  }

  const hasScrollContainer = !!finalScrollContainer;

  return (
    <>
      {isStuck && (
        <FieldErrorMessagesPortal>
          <FieldErrorMessagesStuck
            position={stickyPosition}
            visible={isStuck}
            hasScrollContainer={hasScrollContainer}
            css={{
              zIndex,
              [stickyPosition === 'top' ? 'top' : 'bottom']: 0,
            }}
          >
            <FieldErrorMessagesOriginal isStuck={stickyPosition} ref={stuckRef}>
              {children}
            </FieldErrorMessagesOriginal>
          </FieldErrorMessagesStuck>
        </FieldErrorMessagesPortal>
      )}
      <FieldErrorMessagesOriginal data-alert-container ref={originalRef}>
        {children}
      </FieldErrorMessagesOriginal>
    </>
  );
}

export function FieldErrorMessages(props: FieldErrorMessagesProps) {
  return <FieldErrorMessagesContent {...props} />;
}
